/**
 * Clu's cleanup cards in the dock (W5 design 8.5 and 10.5), read the way the
 * backend writes them: the merge-groups confirmation, the merge it started,
 * and the reads that link to the review of duplicates. The contract is
 * pinned in `__fixtures__/clu-merge-card-contract.json`.
 */
import { canonicalToolName } from '@/components/agent-chat/tool-labels';
import {
  flagLabel,
  keptWords,
  recordCount,
} from '@/containers/agents/duplicate-review/duplicate-review.util';
import {
  REVIEW_OPEN_VIEW,
  type WorkspaceLocation,
} from '@/containers/agents/workspace-location.util';
import {
  cleanupCrmName,
  cleanupObjectNoun,
  joinWords,
  type CleanupCrm,
} from '@/containers/playbooks/custom-builder/cleanup-rules';
import { isRunLive } from '@/containers/agents/run-status.util';
import type { RunCleanup, RunStatus } from '@/types/custom-playbooks';
import type {
  DuplicateGroupRow,
  DuplicateGroupStatus,
} from '@/types/duplicate-groups';
import { asRecord } from '@/components/agent-chat/as-record.util';

/** The confirmed action a merge-groups card asks for. */
export const MERGE_DUPLICATES_ACTION = 'merge_duplicate_groups';

/** The display of the merge a confirmed card started. */
export const MERGE_STARTED_DISPLAY = 'duplicate_merge_started';

/** The display of groups a confirmed card marked not duplicates or skipped. */
export const GROUPS_DECIDED_DISPLAY = 'duplicate_groups_decided';

/** How many of the groups a card lists by name. */
export const MERGE_CARD_GROUPS_SHOWN = 5;

/** The reads whose step links to the review of duplicates. */
const REVIEW_READS = new Set(['find_crm_duplicates', 'read_duplicate_group']);

/** Objects in the order the CRMs list them: accounts before their contacts. */
const OBJECT_ORDER = ['account', 'company', 'contact', 'lead'];

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : null;
}

function crmOf(value: unknown): CleanupCrm | undefined {
  return value === 'salesforce' || value === 'hubspot' ? value : undefined;
}

export interface ObjectCount {
  object: string;
  count: number;
}

/** `{contact: 6, account: 2}` as counts in the CRM's order, zeros left out. */
function objectCounts(value: unknown): ObjectCount[] {
  const record = asRecord(value) ?? {};
  const rank = (object: string) => {
    const index = OBJECT_ORDER.indexOf(object);
    return index === -1 ? OBJECT_ORDER.length : index;
  };
  return Object.entries(record)
    .map(([object, raw]) => ({ object, count: count(raw) ?? 0 }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => rank(a.object) - rank(b.object));
}

/**
 * A group as the card lists it: the review row's own fields. `flags` is the
 * row's too, when the server sends it; none reads as unflagged.
 */
export type MergeCardGroup = Pick<
  DuplicateGroupRow,
  | 'id'
  | 'object'
  | 'displayName'
  | 'memberCount'
  | 'keptId'
  | 'keptReason'
  | 'flags'
>;

function cardGroup(value: unknown): MergeCardGroup | null {
  const group = asRecord(value);
  const id = text(group?.id);
  const object = text(group?.object);
  const memberCount = count(group?.memberCount);
  if (!group || !id || !object || memberCount === null) return null;
  const reason = asRecord(group.keptReason);
  return {
    id,
    object: object as MergeCardGroup['object'],
    displayName: text(group.displayName) ?? null,
    memberCount,
    keptId: text(group.keptId) ?? '',
    keptReason: {
      rule: text(reason?.rule) ?? '',
      detail: text(reason?.detail) ?? '',
    },
    flags: (Array.isArray(group.flags) ? group.flags : []).filter(
      (flag): flag is string => typeof flag === 'string' && flag.length > 0
    ),
  };
}

/** A flagged group's flags as its row reads them: "Names differ, Different accounts". */
export function flagsLine(group: Pick<MergeCardGroup, 'flags'>): string {
  return group.flags.map(flagLabel).join(', ');
}

export interface MergeCardView {
  /** The agent whose groups merge; the dock's agent when the card has none. */
  agentId: string | undefined;
  crmType: CleanupCrm | undefined;
  /** How many groups Confirm merges. */
  groupCount: number;
  byObject: ObjectCount[];
  /** The first groups, by name, with the record each keeps. */
  groups: MergeCardGroup[];
  /** The groups Confirm merges beyond the ones listed. */
  more: number;
  /** An org admin or the CRM's connector (design 5.7). */
  canDecide: boolean;
  connectorName: string | null;
  /**
   * Whether the agent was on when the server built the card. The card reads
   * the agent itself once it loads, since it can be switched while the card
   * waits.
   */
  agentOn: boolean;
}

/**
 * A `merge_duplicate_groups` confirmation as its card reads it; null when its
 * details can't be read, and the card falls back to the server's
 * description. Merge stays off unless the server says the viewer may merge
 * and the agent is on: a missing answer reads as no.
 */
export function mergeCardOf(request: unknown): MergeCardView | null {
  const data = asRecord(request);
  if (!data || data.tool_name !== MERGE_DUPLICATES_ACTION) return null;
  const details = asRecord(data.details);
  const groupCount = count(details?.groupCount);
  if (!details || groupCount === null || groupCount === 0) return null;
  // Flagged groups first, in the server's order otherwise: a flagged group
  // is the one to see before Merge, never behind "N more".
  const read = (Array.isArray(details.groups) ? details.groups : [])
    .map(cardGroup)
    .filter((group): group is MergeCardGroup => group !== null);
  const groups = [
    ...read.filter((group) => group.flags.length > 0),
    ...read.filter((group) => group.flags.length === 0),
  ].slice(0, MERGE_CARD_GROUPS_SHOWN);
  return {
    agentId:
      text(asRecord(data.agent)?.id) ??
      text(asRecord(data.arguments)?.agent_id),
    crmType: crmOf(details.crmType),
    groupCount,
    byObject: objectCounts(details.byObject),
    groups,
    more: Math.max(0, groupCount - groups.length),
    canDecide: details.canDecide === true,
    connectorName: text(details.connectorName) ?? null,
    agentOn: details.agentOn === true,
  };
}

/** "6 contact groups and 2 account groups". */
export function mergeObjectsLine(byObject: readonly ObjectCount[]): string {
  return joinWords(
    byObject.map(
      ({ object, count: groups }) =>
        groups.toLocaleString() +
        ' ' +
        cleanupObjectNoun(object, false) +
        (groups === 1 ? ' group' : ' groups')
    )
  );
}

/** "3 records · Kept: oldest record (Mar 2, 2019)". */
export function keptLine(
  group: Pick<MergeCardGroup, 'memberCount' | 'keptReason'>
): string {
  return recordCount(group.memberCount) + ' · ' + keptWords(group.keptReason);
}

export interface MergeStartedView {
  agentId: string | undefined;
  runId: string | undefined;
  /** The groups the merge took; null when the result doesn't say. */
  groupCount: number | null;
  /** Groups named that weren't open any more, so didn't merge. */
  notMerged: number;
  crmType: CleanupCrm | undefined;
  byObject: ObjectCount[];
}

/** A `duplicate_merge_started` result: the merge run and what it took. */
export function mergeStartedOf(
  display: unknown,
  result: unknown
): MergeStartedView | null {
  const shown = asRecord(display) ?? {};
  const data = asRecord(result) ?? {};
  const runId = text(shown.run_id) ?? text(data.run_id);
  const groupCount = count(data.group_count);
  if (!runId && groupCount === null) return null;
  return {
    agentId: text(shown.agent_id) ?? text(data.agent_id),
    runId,
    groupCount,
    notMerged: count(data.not_merged) ?? 0,
    crmType: crmOf(data.crm_type),
    byObject: objectCounts(data.by_object),
  };
}

/**
 * "Merging 8 groups in Salesforce". A decide that refused every group it
 * named (each `not_open`) approves none and starts no run: "No groups merged".
 */
export function mergeStartedTitle(
  view: Pick<MergeStartedView, 'groupCount' | 'crmType'>
): string {
  const crm = cleanupCrmName(view.crmType);
  if (view.groupCount === null) return 'Merging in ' + crm;
  if (view.groupCount === 0) return 'No groups merged';
  return (
    'Merging ' +
    view.groupCount.toLocaleString() +
    (view.groupCount === 1 ? ' group' : ' groups') +
    ' in ' +
    crm
  );
}

/* ── How the merge went (bug 14 of the W5 live test) ─────────────────── */

/** How many groups a merge left for later the card reads again. */
export const MERGE_WATCHED_GROUPS = 5;

/** How often the card reads a group left for later again, while it waits. */
export const MERGE_WATCH_MS = 30_000;

/**
 * Why a merge run leaves a group whose call may have reached the CRM: the
 * attempt died or lost the group mid merge, so a recovery run verifies it
 * before anything is sent again (design 6.6, `crm_merge_service.py`).
 */
const INTERRUPTED_CODES: ReadonlySet<string> = new Set([
  'merge_in_progress',
  'lease_lost',
  'verify_failed',
  'call_fence',
  'recovery_only',
]);

/** A group a merge run left for later, and why (its record's code). */
export interface DeferredGroup {
  id: string;
  code: string;
}

/**
 * The groups a merge run left for later: its records the run filtered with
 * outcome `deferred` (`crm_merge.py` `record_landing`), each naming its
 * group (`entity.id`).
 */
export function deferredGroupsOf(
  records: readonly { entity?: unknown; error?: unknown }[]
): DeferredGroup[] {
  const out: DeferredGroup[] = [];
  for (const record of records) {
    const error = asRecord(record.error);
    const id = text(asRecord(record.entity)?.id);
    if (!id || error?.outcome !== 'deferred') continue;
    out.push({ id, code: text(error.code) ?? 'deferred' });
  }
  return out;
}

export interface MergeProgressView {
  title: string;
  /** How it went, a line each: "Interrupted, then verified", "1 failed". */
  lines: string[];
  /** A group left for later still waits, so the card reads them again. */
  watching: boolean;
}

function groupsWord(count: number): string {
  return count.toLocaleString() + (count === 1 ? ' group' : ' groups');
}

/**
 * How the merge a card started went. While its run is live: "Merging 8
 * groups in Salesforce". Once it ends, its own counts plus the groups it
 * left for later, read again: a group whose attempt was interrupted reads
 * "Merge interrupted" and "Verifying 1 group" until a recovery run lands it,
 * then "Merged 1 group in Salesforce" and "Interrupted, then verified".
 * `deferred` is undefined while the run's records load; `groups` holds the
 * groups read again, undefined while one loads.
 */
export function mergeProgressOf(
  started: Pick<MergeStartedView, 'groupCount' | 'crmType' | 'runId'>,
  run:
    | { status: RunStatus; cleanup?: Partial<RunCleanup> | null }
    | null
    | undefined,
  deferred: readonly DeferredGroup[] | undefined,
  groups: Readonly<
    Record<string, { status: DuplicateGroupStatus } | undefined>
  > = {}
): MergeProgressView {
  const starting: MergeProgressView = {
    title: mergeStartedTitle(started),
    lines: [],
    watching: false,
  };
  if (!started.runId || !run || isRunLive(run.status)) return starting;
  const cleanup = run.cleanup ?? {};
  let merged = count(cleanup.merged) ?? 0;
  let failed = count(cleanup.failed) ?? 0;
  let skipped = count(cleanup.skipped) ?? 0;
  const settled = merged + failed + skipped;
  // The records say which groups the run left; until they load, a run that
  // didn't settle every group still reads as merging.
  if (deferred === undefined)
    return started.groupCount !== null && settled >= started.groupCount
      ? mergeProgressOf(started, run, [], groups)
      : starting;

  let recovered = 0;
  let later = 0;
  let verifying = 0;
  let waiting = 0;
  let reopened = 0;
  let unread = 0;
  for (const group of deferred) {
    const interrupted = INTERRUPTED_CODES.has(group.code);
    if (!(group.id in groups)) {
      unread += 1;
      continue;
    }
    const now = groups[group.id];
    switch (now?.status) {
      case 'merged':
        merged += 1;
        if (interrupted) recovered += 1;
        else later += 1;
        break;
      case 'failed':
        failed += 1;
        break;
      case 'skipped_changed':
        skipped += 1;
        break;
      case 'open':
        reopened += 1;
        break;
      case 'dismissed':
      case 'superseded':
        break;
      // Approved, merging, being verified, or still loading.
      default:
        if (interrupted) verifying += 1;
        else waiting += 1;
    }
  }

  const crm = cleanupCrmName(started.crmType);
  const title =
    verifying > 0
      ? 'Merge interrupted'
      : waiting > 0
        ? mergeStartedTitle(started)
        : merged > 0
          ? 'Merged ' + groupsWord(merged) + ' in ' + crm
          : 'No groups merged';
  const lines: string[] = [];
  if (recovered > 0)
    lines.push(
      recovered === 1
        ? 'Interrupted, then verified'
        : recovered.toLocaleString() + ' interrupted, then verified'
    );
  if (verifying > 0) lines.push('Verifying ' + groupsWord(verifying));
  if (later > 0) lines.push(later.toLocaleString() + ' merged in a later run');
  if (waiting > 0) lines.push(groupsWord(waiting) + ' waiting to merge');
  if (unread > 0) lines.push(groupsWord(unread) + ' left for a later run');
  if (failed > 0) lines.push(failed.toLocaleString() + ' failed');
  if (skipped > 0) lines.push(skipped.toLocaleString() + ' skipped');
  if (reopened > 0) lines.push(reopened.toLocaleString() + ' back in review');
  return { title, lines, watching: verifying + waiting > 0 };
}

/** "1 group not merged". */
export function notMergedLine(notMerged: number): string {
  return (
    notMerged.toLocaleString() +
    (notMerged === 1 ? ' group' : ' groups') +
    ' not merged'
  );
}

/**
 * Where "Review duplicates" opens: the Runs tab's review of open and merging
 * groups, with one scan's groups highlighted when a run is named. A merge
 * run names no scan, so the merge card opens the review without one.
 */
export function reviewLocation(runId?: string): WorkspaceLocation {
  return {
    tab: 'runs',
    view: REVIEW_OPEN_VIEW,
    ...(runId ? { run: runId } : {}),
  };
}

/**
 * The review a duplicate read links to: its agent and the scan it was about.
 * Null for any other tool, and for a read that failed.
 */
export function duplicatesReviewOf(
  toolName: string,
  result: unknown
): { agentId: string | undefined; runId: string | undefined } | null {
  if (!REVIEW_READS.has(canonicalToolName(toolName))) return null;
  const data = asRecord(result) ?? {};
  if (typeof data.error === 'string' && data.error) return null;
  return { agentId: text(data.agent_id), runId: text(data.run_id) };
}
